import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Stethoscope, CheckCircle, Sparkles, BookOpen, Users, Shield, Award, ArrowRight } from 'lucide-react';

/**
 * Standalone About / Mission page for the MedCare National Board of Nursing
 * division of the association. Both this page and the Vital Health page
 * link to /association for the actual membership signup (single combined
 * $9.99/mo subscription covers both divisions).
 */
const BoardOfNursingPage = () => (
  <div style={{ minHeight: '100vh', background: '#0f172a', color: '#e2e8f0' }}>
    <div style={{ padding: '20px 40px', display: 'flex', gap: 20, alignItems: 'center' }}>
      <Link to="/" style={{ display: 'inline-flex', gap: 8, color: '#cbd5e1', textDecoration: 'none' }}>
        <ArrowLeft size={18} /> Back to Home
      </Link>
      <Link to="/association" style={{ marginLeft: 'auto', color: '#5eead4', textDecoration: 'none' }}>
        ← View combined association &amp; join
      </Link>
    </div>

    <section style={{ padding: '40px 40px 60px', maxWidth: 1000, margin: '0 auto', textAlign: 'center' }}>
      <div style={{ width: 80, height: 80, borderRadius: 20, background: '#ccfbf1', color: '#0d9488', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 18px' }}>
        <Stethoscope size={42} />
      </div>
      <div style={{ display: 'inline-block', padding: '6px 14px', borderRadius: 999, background: 'rgba(13,148,136,0.18)', color: '#5eead4', fontSize: '0.72rem', letterSpacing: 1.2, textTransform: 'uppercase', marginBottom: 14 }}>
        Division of the MedCare Association
      </div>
      <h1 data-testid="board-of-nursing-title" style={{ fontSize: '2.5rem', fontWeight: 800, color: '#f8fafc', margin: 0, lineHeight: 1.18 }}>
        MedCare National Board of Nursing
      </h1>
      <p style={{ color: '#cbd5e1', maxWidth: 720, margin: '20px auto 0', lineHeight: 1.65, fontSize: '1.05rem' }}>
        A workforce-development credentialing body for graduates of USDOL-registered
        nursing-pathway apprenticeships — CNA, Practical Nurse (PN), EMT, Phlebotomy,
        Medical Aide, and the new Medical Billing &amp; Coding programs.
      </p>
    </section>

    <section style={{ padding: '40px 40px', maxWidth: 1000, margin: '0 auto' }}>
      <h2 style={{ fontSize: '1.5rem', color: '#f8fafc' }}>About us</h2>
      <p style={{ color: '#cbd5e1', lineHeight: 1.7 }}>
        The MedCare National Board of Nursing was established as a workforce-development
        credentialing body for apprenticeship graduates who have completed the
        rigorous, USDOL-registered didactic and clinical hours required by the
        MedCare Nursing Institute &amp; Training Academy. Our role is to maintain
        a verifiable register of nursing-pathway apprenticeship graduates, track
        their continuing-education credits, and connect them to ongoing career
        services.
      </p>
      <p style={{ color: '#cbd5e1', lineHeight: 1.7, marginTop: 16 }}>
        We exist <strong>alongside</strong> — never in place of — the State Boards
        of Nursing and State Departments of Health. We do not issue state licenses.
        We register and support apprenticeship-pathway graduates as they pursue
        those licenses through the proper public channels (NCLEX-PN, NCLEX-RN,
        state CNA registries, the EMT National Registry, and others).
      </p>

      <h2 style={{ fontSize: '1.5rem', color: '#f8fafc', marginTop: 36 }}>Mission statement</h2>
      <p style={{ color: '#cbd5e1', lineHeight: 1.7, fontStyle: 'italic' }}>
        &ldquo;To stand alongside the public licensing bodies as a recognized credentialing
        partner for the apprenticeship era — verifying training, tracking continuing
        education, and connecting nursing-pathway graduates to the lifelong career
        community they deserve.&rdquo;
      </p>
    </section>

    <section style={{ padding: '40px 40px', maxWidth: 1000, margin: '0 auto' }}>
      <h2 style={{ fontSize: '1.5rem', color: '#f8fafc' }}>What we do</h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))', gap: 18, marginTop: 16 }}>
        {[
          { icon: Award, t: 'Apprenticeship Credentialing', d: 'Recognize and register every MedCare apprenticeship graduate.' },
          { icon: BookOpen, t: 'CE Tracking', d: 'Hold the central record of every member’s continuing-education hours.' },
          { icon: Users, t: 'Member Directory', d: 'Maintain a verifiable, public-facing member register.' },
          { icon: Shield, t: 'Standards & Ethics', d: 'Publish a code of conduct shared by both association divisions.' },
        ].map((b) => {
          const I = b.icon;
          return (
            <div key={b.t} style={{ background: 'rgba(255,255,255,0.04)', padding: 18, borderRadius: 12, border: '1px solid rgba(255,255,255,0.06)' }}>
              <I size={22} color="#5eead4" />
              <div style={{ color: '#f8fafc', fontWeight: 700, marginTop: 8 }}>{b.t}</div>
              <p style={{ color: '#94a3b8', marginTop: 4, lineHeight: 1.55, fontSize: '0.92rem' }}>{b.d}</p>
            </div>
          );
        })}
      </div>
    </section>

    <section style={{ padding: '0 40px 80px', maxWidth: 1000, margin: '0 auto', textAlign: 'center', marginTop: 30 }}>
      <Link to="/association" data-testid="board-of-nursing-join-link" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, padding: '16px 36px', borderRadius: 999, background: 'linear-gradient(90deg,#14b8a6,#0d9488)', color: 'white', fontWeight: 700, textDecoration: 'none', fontSize: '1rem' }}>
        Become a Member — $9.99/month <ArrowRight size={20} />
      </Link>
      <p style={{ marginTop: 12, fontSize: '0.85rem', color: '#94a3b8' }}>
        A single membership covers both the Board of Nursing and the Vital Health Sciences divisions.
      </p>
    </section>
    <div style={{ padding: '24px 40px', textAlign: 'center', color: '#64748b', fontSize: '0.85rem' }}>
      MedCare Nursing Institute &amp; Training Academy · 3150 E. Shelby Dr, Memphis TN 38118 · 901.625.1201
    </div>
  </div>
);

export default BoardOfNursingPage;
